import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { toast } from 'dashboard/helper/toast';
import { formatPauseClock, formatPauseLimit } from './pauseClock';
import { usePauseReasons } from './usePauseReasons';

// Pausa do usuário logado, pronta pra tela: motivo, cronômetro, limite,
// estouro e as ações. Quem usa: o chip da barra superior, o menu de status do
// perfil e o discador — todos leem o mesmo store, então mostram a mesma pausa.
// Pausado = disponibilidade Ocupado (por qualquer caminho).
export function useAgentPause() {
  const { t } = useI18n();
  const store = useStore();
  const availability = useMapGetter('getCurrentUserAvailability');
  const current = useMapGetter('agentPauses/getCurrentPause');
  const uiFlags = useMapGetter('agentPauses/getUIFlags');
  const { options, reasonLabel, limitSeconds, isOverdue } = usePauseReasons();

  const isPaused = computed(() => availability.value === 'busy');
  const reason = computed(() => (isPaused.value ? current.value.reason : ''));
  const isChanging = computed(() => uiFlags.value.isChangingPause);

  const now = ref(Date.now());
  let timer = null;
  const stopClock = () => {
    clearInterval(timer);
    timer = null;
  };
  watch(
    isPaused,
    paused => {
      if (!paused) {
        stopClock();
        return;
      }
      if (timer) return;
      now.value = Date.now();
      timer = setInterval(() => {
        now.value = Date.now();
      }, 1000);
    },
    { immediate: true }
  );
  onUnmounted(stopClock);

  const elapsedSeconds = computed(() => {
    const startedAt = current.value.startedAt;
    if (!isPaused.value || !startedAt) return 0;
    return Math.max(0, (now.value - startedAt) / 1000);
  });

  const overdue = computed(() => isOverdue(reason.value, elapsedSeconds.value));
  const label = computed(() =>
    reason.value
      ? reasonLabel(reason.value)
      : t('AGENT_PAUSE.BAR.ON_PAUSE_NO_REASON')
  );
  const clock = computed(() => formatPauseClock(elapsedSeconds.value));
  const limitLabel = computed(() => {
    const limit = limitSeconds(reason.value);
    return limit === null ? '' : formatPauseLimit(limit);
  });
  const limitTooltip = computed(() => {
    if (!limitLabel.value) return '';
    const key = overdue.value
      ? 'AGENT_PAUSE.BAR.OVERDUE'
      : 'AGENT_PAUSE.BAR.LIMIT';
    return t(key, { limit: limitLabel.value });
  });

  // Devolve se pausou: o diálogo de motivo só fecha no sucesso.
  const pause = async key => {
    // Mesmo motivo já ativo: o servidor mantém o período (transition! é
    // idempotente); reenviar só zeraria o cronômetro da tela.
    if (isPaused.value && key === reason.value) return true;
    try {
      await store.dispatch('agentPauses/pause', key);
      return true;
    } catch {
      toast.error(t('AGENT_PAUSE.BAR.ERROR'));
      // Causa mais comum: o motivo foi arquivado depois que esta tela
      // carregou (422). Recarrega a lista pra ele sumir do menu.
      store.dispatch('agentPauses/fetchReasons').catch(() => {
        toast.error(t('AGENT_PAUSE.REASONS.FETCH_ERROR'));
      });
      return false;
    }
  };

  const resume = async () => {
    try {
      await store.dispatch('agentPauses/resume');
    } catch {
      toast.error(t('AGENT_PAUSE.BAR.ERROR'));
    }
  };

  const optionLimitLabel = option =>
    option.limitSeconds ? formatPauseLimit(option.limitSeconds) : '';

  return {
    options,
    isPaused,
    reason,
    label,
    clock,
    limitLabel,
    limitTooltip,
    isOverdue: overdue,
    isChanging,
    pause,
    resume,
    optionLimitLabel,
  };
}
